import { useState, useEffect } from 'react';
import gameService from '../gameService';
import { useGameDispatch } from '../GameProvider';
import { setRoom } from '../gameActions';
import { storageKey } from '../../storageKeys';
import './GameEntry.css';

const GAME_MODES = [
  { id: 'basic', label: 'Basic', description: 'Majority rows wins' },
  { id: 'nem', label: 'Nem', description: 'Card exchange + majority' },
  { id: 'chi', label: 'Chi', description: 'Per-row scoring with bonuses' },
  { id: 'nemchi', label: 'Nem Chi', description: 'Card exchange + per-row scoring' },
];

const DEFAULT_RULES = {
  violationHelp: true,
  sweepBonus: true,
  individualRoundHouse: false,
  autoWinEnabled: true,
  autoWinValue: 3,
  bonusHandsEnabled: true,
  middleMultiplierEnabled: true,
};

// Read once on module load so the fresh-start escape path runs before any
// React state initializers look at the stored session. Mirrors the pattern
// used in gameConfig.js for testTimer/testOvertime URL params.
const _entryUrlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
const FRESH_START_REQUESTED =
  !!_entryUrlParams && (_entryUrlParams.get('fresh') === '1' || _entryUrlParams.get('reset') === '1');

if (FRESH_START_REQUESTED) {
  // Wipe client-side session state immediately — before GameEntry mounts — so
  // the first paint is always the fresh entry screen, never the reconnect prompt.
  try { localStorage.removeItem(storageKey('xapxam-session')); } catch {}
  try { sessionStorage.removeItem(storageKey('xapxam_roomCode')); } catch {}
  // Strip the query param so refreshing or sharing the URL doesn't keep re-wiping.
  try {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('fresh');
    cleanUrl.searchParams.delete('reset');
    window.history.replaceState({}, '', cleanUrl.toString());
  } catch {}
}

export default function GameEntry({ avatars, onEnterGame, onScoreTracker, pwaInstall }) {
  const dispatch = useGameDispatch();
  const [screen, setScreen] = useState('main'); // 'main' | 'multiplayer' | 'create' | 'join'
  const [name, setName] = useState(() => localStorage.getItem(storageKey('xapxam-name')) || '');
  const [avatar, setAvatar] = useState(() => localStorage.getItem(storageKey('xapxam-avatar')) || 'fish.jpg');
  const [joinCode, setJoinCode] = useState('');
  const [error, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [storedSession, setStoredSession] = useState(null);

  // Check for existing session on mount. Skip entirely if ?fresh=1 / ?reset=1
  // was present — the module-level block already wiped the stored session.
  useEffect(() => {
    if (FRESH_START_REQUESTED) return;
    const session = gameService.getStoredSession();
    if (!session) return;
    gameService.checkSessionValid(session.roomCode, session.uid).then(valid => {
      if (valid) setStoredSession(session);
      else gameService.clearStoredSession();
    });
  }, []);

  const saveName = (n) => {
    setName(n);
    localStorage.setItem(storageKey('xapxam-name'), n);
  };

  const saveAvatar = (a) => {
    setAvatar(a);
    localStorage.setItem(storageKey('xapxam-avatar'), a);
  };

  const handleCreate = async (gameMode) => {
    if (!name.trim()) { setLocalError('Enter your name'); return; }
    setLoading(true);
    setLocalError('');
    try {
      const rules = { ...DEFAULT_RULES };
      if (gameMode === 'nemchi') {
        rules.autoWinEnabled = false;
      }
      const { gameId, uid } = await gameService.createGame(gameMode, rules, name.trim(), avatar);
      dispatch(setRoom(gameId, uid));
      onEnterGame(gameId, uid);
    } catch (err) {
      setLocalError(err.message);
    }
    setLoading(false);
  };

  const handleJoin = async () => {
    if (!name.trim()) { setLocalError('Enter your name'); return; }
    if (!joinCode.trim()) { setLocalError('Enter room code'); return; }
    setLoading(true);
    setLocalError('');
    try {
      const result = await gameService.joinGame(joinCode, name.trim(), avatar);
      if (!result.success) { setLocalError(result.error); setLoading(false); return; }
      dispatch(setRoom(result.gameId, result.uid));
      onEnterGame(result.gameId, result.uid);
    } catch (err) {
      setLocalError(err.message);
    }
    setLoading(false);
  };

  const handleRejoin = async () => {
    if (!storedSession) return;
    setLoading(true);
    try {
      const result = await gameService.joinGame(storedSession.roomCode, name.trim() || 'Player', avatar);
      if (result.success) {
        dispatch(setRoom(result.gameId, result.uid));
        onEnterGame(result.gameId, result.uid);
      } else {
        setStoredSession(null);
        gameService.clearStoredSession();
        setLocalError('Game has ended');
      }
    } catch {
      setStoredSession(null);
      gameService.clearStoredSession();
    }
    setLoading(false);
  };

  const handleLeaveSession = () => {
    setStoredSession(null);
    gameService.freshStart();
  };

  // Reconnect prompt
  if (storedSession) {
    return (
      <div className="game-entry" data-testid="game-entry" data-screen="reconnect">
        <div className="game-entry__reconnect" data-testid="entry-reconnect">
          <h2>You have an active game!</h2>
          <p className="game-entry__room-info">Room: <strong data-testid="entry-reconnect-code">{storedSession.roomCode}</strong></p>
          <div className="game-entry__actions">
            <button
              className="btn btn--primary"
              data-testid="entry-reconnect-rejoin"
              onClick={handleRejoin}
              disabled={loading}
            >
              {loading ? 'Rejoining...' : 'Rejoin Game'}
            </button>
            <button
              className="btn btn--secondary"
              data-testid="entry-reconnect-start-fresh"
              onClick={handleLeaveSession}
            >
              Start Fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry" data-testid="game-entry" data-screen={screen}>
      {screen === 'main' && (
        <div className="game-entry__main" data-testid="entry-main">
          <h1 className="game-entry__title">Xap Xam</h1>

          <div className="game-entry__lobby-actions">
            <button
              className="btn btn--primary game-entry__lobby-btn"
              data-testid="entry-multiplayer-btn"
              onClick={() => setScreen('multiplayer')}
            >
              Multiplayer
            </button>
            {onScoreTracker && (
              <button
                className="btn btn--secondary game-entry__lobby-btn game-entry__score-tracker-btn"
                data-testid="entry-score-tracker-btn"
                onClick={onScoreTracker}
              >
                Scorekeeper
              </button>
            )}
            {pwaInstall && !pwaInstall.isInstalled && (
              <button
                className="btn btn--secondary game-entry__lobby-btn game-entry__install-btn"
                data-testid="entry-install-btn"
                onClick={pwaInstall.install}
              >
                Install Xap Xam
              </button>
            )}
          </div>
          {pwaInstall?.isInstalled && (
            <p className="game-entry__installed" data-testid="entry-installed-state">
              Installed app mode
            </p>
          )}
          {pwaInstall?.message && (
            <p className="game-entry__install-message" role="status">
              {pwaInstall.message}
            </p>
          )}
        </div>
      )}

      {screen === 'multiplayer' && (
        <div className="game-entry__multiplayer" data-testid="entry-multiplayer">
          <h1 className="game-entry__title">Xap Xam</h1>

          <div className="game-entry__name-section">
            <input
              className="game-entry__input"
              data-testid="entry-name-input"
              placeholder="Your name"
              value={name}
              onChange={e => saveName(e.target.value)}
              maxLength={20}
            />
          </div>

          {/* Avatar picker */}
          <div className="game-entry__avatars">
            {(avatars || []).map(a => {
              const file = typeof a === 'string' ? a : a.file;
              const name = typeof a === 'string' ? a : (a.name || a.file);
              return (
                <img
                  key={file}
                  src={`${process.env.PUBLIC_URL}/avatars/${file}`}
                  alt={name}
                  className={`game-entry__avatar ${avatar === file ? 'game-entry__avatar--selected' : ''}`}
                  onClick={() => saveAvatar(file)}
                />
              );
            })}
          </div>

          <div className="game-entry__actions">
            <button
              className="btn btn--primary"
              data-testid="entry-create-room-btn"
              onClick={() => setScreen('create')}
            >
              Create Room
            </button>
            <button
              className="btn btn--secondary"
              data-testid="entry-join-room-btn"
              onClick={() => setScreen('join')}
            >
              Join Room
            </button>
          </div>

          {error && <p className="game-entry__error" data-testid="entry-error">{error}</p>}

          <button
            className="btn btn--secondary game-entry__back-btn"
            data-testid="entry-multiplayer-back-btn"
            onClick={() => setScreen('main')}
          >
            Back
          </button>
        </div>
      )}

      {screen === 'create' && (
        <div className="game-entry__create" data-testid="entry-create">
          <h2>Select Game Mode</h2>
          <div className="game-entry__modes">
            {GAME_MODES.map(mode => (
              <button
                key={mode.id}
                className="game-entry__mode-btn"
                data-testid={`entry-mode-${mode.id}`}
                onClick={() => handleCreate(mode.id)}
                disabled={loading}
              >
                <strong>{mode.label}</strong>
                <span>{mode.description}</span>
              </button>
            ))}
          </div>
          <button
            className="btn btn--secondary"
            data-testid="entry-create-back-btn"
            onClick={() => setScreen('multiplayer')}
            disabled={loading}
          >
            Back
          </button>
          {loading && (
            <p className="game-entry__hint" data-testid="entry-create-loading">Creating room&hellip;</p>
          )}
          {error && <p className="game-entry__error" data-testid="entry-error">{error}</p>}
        </div>
      )}

      {screen === 'join' && (
        <div className="game-entry__join" data-testid="entry-join">
          <h2>Join Room</h2>
          <input
            className="game-entry__input game-entry__code-input"
            data-testid="entry-join-code-input"
            placeholder="ROOM CODE"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            maxLength={5}
          />
          <div className="game-entry__actions">
            <button
              className="btn btn--primary"
              data-testid="entry-join-submit-btn"
              onClick={handleJoin}
              disabled={loading}
            >
              {loading ? 'Joining...' : 'Join'}
            </button>
            <button
              className="btn btn--secondary"
              data-testid="entry-join-back-btn"
              onClick={() => setScreen('multiplayer')}
              disabled={loading}
            >
              Back
            </button>
          </div>
          {error && <p className="game-entry__error" data-testid="entry-error">{error}</p>}
        </div>
      )}
    </div>
  );
}
